"use client";

import { useEffect } from "react";

/**
 * iOS-style edge-swipe-back gesture.
 *
 * The hook itself is brand-agnostic mechanics — the layout-mounted
 * listener is started by OwletNavDirectionTracker, which is only
 * rendered when BRAND === "owlet" (see app/layout.tsx). Kinsa never
 * starts the listener, so the hook is inert there.
 *
 * Opt-in per page: a page that renders a back chevron or close button
 * calls `useEdgeSwipeBack(handler)`, and the listener invokes that
 * handler when the user completes a left-edge horizontal swipe.
 *
 * Only one handler can be active at a time — that matches reality
 * (only one route is visible at a time). On unmount the page clears
 * its registration so the next route opts in afresh; routes without
 * a back/close button leave the slot empty and the gesture is a
 * no-op.
 *
 * The gesture is a TRIGGER, not a follow-finger drag. We do not
 * translate the page with the finger; the existing back slide (driven
 * by popstate → next-view-transitions) plays the visual. A real
 * UINavigationController-style interactive drag is a separate spike —
 * see the PR description.
 *
 * Hit zone: 24 px from the left edge. Commit threshold: 60 px of
 * horizontal travel with |Δy| < Δx and the gesture starting within
 * 600 ms. Anything else is ignored. Inputs from `mouse` and `pen`
 * pointer types are ignored — we only want touch.
 */

let activeHandler: (() => void) | null = null;

export function useEdgeSwipeBack(onBack: () => void): void {
  useEffect(() => {
    activeHandler = onBack;
    return () => {
      if (activeHandler === onBack) {
        activeHandler = null;
      }
    };
  }, [onBack]);
}

const EDGE_PX = 24;
const COMMIT_PX = 60;
const MAX_MS = 600;

/**
 * Document-level Pointer Events listener. Mount once at the root
 * (currently invoked from OwletNavDirectionTracker, which only mounts
 * under BRAND === "owlet").
 */
export function startEdgeSwipeListener(): () => void {
  if (typeof document === "undefined") return () => {};

  let startX = 0;
  let startY = 0;
  let startedAt = 0;
  let tracking = false;
  let pointerId: number | null = null;

  const reset = () => {
    tracking = false;
    pointerId = null;
  };

  const onPointerDown = (event: PointerEvent) => {
    if (event.pointerType !== "touch") return;
    if (!activeHandler) return;
    if (event.clientX > EDGE_PX) return;

    startX = event.clientX;
    startY = event.clientY;
    startedAt = Date.now();
    pointerId = event.pointerId;
    tracking = true;
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!tracking || event.pointerId !== pointerId) return;

    const dx = event.clientX - startX;
    const dy = event.clientY - startY;
    const elapsed = Date.now() - startedAt;

    // If the user is panning vertically (likely a scroll), abandon
    // before we can mis-fire on the next horizontal jitter.
    if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) {
      reset();
      return;
    }

    // Time out the gesture so a slow drift across the screen doesn't
    // ever count as a swipe.
    if (elapsed > MAX_MS) {
      reset();
      return;
    }

    if (dx >= COMMIT_PX) {
      // Fire once and stop tracking until pointerup. Don't preventDefault
      // — letting the underlying page receive the event is harmless and
      // avoids fighting Vaul/iframe gestures.
      const handler = activeHandler;
      reset();
      handler?.();
    }
  };

  const onPointerEnd = (event: PointerEvent) => {
    if (event.pointerId === pointerId) reset();
  };

  document.addEventListener("pointerdown", onPointerDown);
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerEnd);
  document.addEventListener("pointercancel", onPointerEnd);

  return () => {
    document.removeEventListener("pointerdown", onPointerDown);
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerEnd);
    document.removeEventListener("pointercancel", onPointerEnd);
  };
}
